import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  createNarrative,
  updateNarrative,
  narrativeAiAction,
  getNarrativeVersions,
  revertNarrative,
} from '../../utils/financialAnalystApi';
import './FinancialAnalyst.css';

const ACTION_PROGRESS = {
  polish: 'polishing',
  expand: 'expanding',
  tighten: 'tightening',
  incorporate: 'cleaning up edits',
  fact_check: 'checking facts',
  add_color: 'adding color',
  generate: 'drafting',
};

const getField = (obj, camel, snake, fallback = '') => (
  obj?.[camel] ?? obj?.[snake] ?? fallback
);

const NarrativeCard = ({ narrative, onUpdate, isLocked, packageId }) => {
  const [content, setContent] = useState(narrative?.content || '');
  const [version, setVersion] = useState(narrative?.version || 1);
  const [versions, setVersions] = useState(null);
  const [showVersions, setShowVersions] = useState(false);
  const [aiLoading, setAiLoading] = useState(null);
  const [selectionToolbar, setSelectionToolbar] = useState(null);
  const [selectionRange, setSelectionRange] = useState(null);
  const [saveStatus, setSaveStatus] = useState('saved');
  const [error, setError] = useState(null);
  const editorRef = useRef(null);
  const saveTimerRef = useRef(null);
  const lastSavedContentRef = useRef(narrative?.content || '');

  useEffect(() => {
    const nextContent = narrative?.content || '';
    setContent(nextContent);
    setVersion(narrative?.version || 1);
    setVersions(null);
    setShowVersions(false);
    setSaveStatus('saved');
    setError(null);
    lastSavedContentRef.current = nextContent;
    if (editorRef.current && editorRef.current.innerText !== nextContent) {
      editorRef.current.innerText = nextContent;
    }
  }, [narrative?.id, narrative?.content, narrative?.version]);

  useEffect(() => () => {
    clearTimeout(saveTimerRef.current);
  }, []);

  const applyNarrativeResult = useCallback((updated) => {
    const nextContent = updated?.content ?? '';
    setContent(nextContent);
    setVersion(updated?.version || 1);
    setVersions(null);
    setSaveStatus('saved');
    setError(null);
    lastSavedContentRef.current = nextContent;
    if (editorRef.current) {
      editorRef.current.innerText = nextContent;
    }
  }, []);

  const saveContent = useCallback(async (text) => {
    if (!narrative?.id || isLocked || text === lastSavedContentRef.current) {
      setSaveStatus('saved');
      return null;
    }

    setSaveStatus('saving');
    setError(null);
    try {
      const result = await updateNarrative(narrative.id, { content: text });
      const updated = result.narrative || result;
      lastSavedContentRef.current = updated?.content ?? text;
      setContent(updated?.content ?? text);
      setVersion(updated?.version || version);
      setVersions(null);
      setSaveStatus('saved');
      if (onUpdate) onUpdate();
      return updated;
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to save narrative.';
      console.error('Failed to save narrative:', err);
      setError(message);
      setSaveStatus('error');
      return null;
    }
  }, [isLocked, narrative?.id, onUpdate, version]);

  // Auto-save on content change.
  const handleInput = useCallback(() => {
    const text = editorRef.current?.innerText || '';
    setContent(text);
    setError(null);

    if (isLocked) return;
    setSaveStatus(text === lastSavedContentRef.current ? 'saved' : 'dirty');
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveContent(text);
    }, 900);
  }, [isLocked, saveContent]);

  // Selection handling for floating toolbar
  const handleMouseUp = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.toString().trim().length > 0 && editorRef.current?.contains(selection.anchorNode)) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      const editorRect = editorRef.current.getBoundingClientRect();

      setSelectionToolbar({
        top: rect.top - editorRect.top - 44,
        left: rect.left - editorRect.left + rect.width / 2,
      });

      const fullText = editorRef.current.innerText;
      const selText = selection.toString();
      const startIdx = fullText.indexOf(selText);
      if (startIdx >= 0) {
        setSelectionRange([startIdx, startIdx + selText.length]);
      }
    } else {
      setSelectionToolbar(null);
      setSelectionRange(null);
    }
  }, []);

  const handleMouseDown = useCallback(() => {
    setSelectionToolbar(null);
  }, []);

  // AI Actions
  const handleAiAction = async (action) => {
    if (aiLoading || isLocked || !narrative?.id) return;
    setAiLoading(action);
    setSelectionToolbar(null);
    setError(null);

    try {
      clearTimeout(saveTimerRef.current);
      const latestText = editorRef.current?.innerText || content;
      if (latestText !== lastSavedContentRef.current) {
        await saveContent(latestText);
      }

      const payload = { action };
      if (selectionRange) {
        payload.selectionStart = selectionRange[0];
        payload.selectionEnd = selectionRange[1];
      }

      const result = await narrativeAiAction(narrative.id, payload);
      if (result.narrative) {
        applyNarrativeResult(result.narrative);
        if (onUpdate) onUpdate();
      }
    } catch (err) {
      const message = err.response?.data?.error || err.message || `AI action ${action} failed.`;
      console.error(`AI action ${action} failed:`, err);
      setError(message);
    }
    setAiLoading(null);
    setSelectionRange(null);
  };

  // Version history
  const loadVersions = async () => {
    if (versions) {
      setShowVersions(!showVersions);
      return;
    }
    try {
      const data = await getNarrativeVersions(narrative.id);
      setVersions(data.versions || []);
      setShowVersions(true);
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to load versions.';
      console.error('Failed to load versions:', err);
      setError(message);
    }
  };

  const handleRevert = async (ver) => {
    if (isLocked) return;
    try {
      const result = await revertNarrative(narrative.id, ver);
      if (result.narrative) {
        applyNarrativeResult(result.narrative);
        if (onUpdate) onUpdate();
      }
      setShowVersions(false);
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to revert narrative.';
      console.error('Failed to revert:', err);
      setError(message);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
  };

  const sectionType = getField(narrative, 'sectionType', 'section_type', 'custom');
  const sectionTitle = getField(narrative, 'sectionTitle', 'section_title')
    || sectionType?.replace(/_/g, ' ')
    || 'Narrative';

  const handleGenerateDraft = async () => {
    if (aiLoading || isLocked || !packageId || !sectionType) return;
    setAiLoading('generate');
    setError(null);
    try {
      const result = await createNarrative(packageId, { sectionType, sectionTitle, content: '' });
      if (result.narrative) {
        applyNarrativeResult(result.narrative);
        if (onUpdate) onUpdate();
      }
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to generate narrative draft.';
      console.error('Failed to generate narrative draft:', err);
      setError(message);
    } finally {
      setAiLoading(null);
    }
  };

  return (
    <div className="fa-narrative-card">
      {/* Header */}
      <div className="fa-narrative-header">
        <div className="fa-narrative-header-left">
          <span className="fa-narrative-badge">AI Narrative</span>
          <h3 className="fa-narrative-title">{sectionTitle}</h3>
          <span className="fa-narrative-version">· Draft v{version}</span>
          <span className={`fa-save-indicator ${saveStatus}`}>
            {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'dirty' ? 'Unsaved' : saveStatus === 'error' ? 'Save failed' : 'Saved'}
          </span>
        </div>
        <div className="fa-narrative-header-right">
          <button className="fa-btn fa-btn-sm fa-btn-ghost" onClick={handleCopy} title="Copy text">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
            Copy
          </button>
        </div>
      </div>

      {/* Editor */}
      <div className="fa-narrative-editor-wrap">
        <div
          ref={editorRef}
          className="fa-narrative-body"
          contentEditable={!isLocked}
          suppressContentEditableWarning
          onInput={handleInput}
          onMouseUp={handleMouseUp}
          onMouseDown={handleMouseDown}
          data-placeholder="AI-generated narrative will appear here..."
        />

        {/* Floating selection toolbar */}
        {selectionToolbar && !isLocked && (
          <div
            className="fa-selection-toolbar"
            style={{ top: selectionToolbar.top, left: selectionToolbar.left }}
          >
            <button className="fa-btn" onClick={() => handleAiAction('polish')}>
              {aiLoading === 'polish' ? '...' : 'Polish'}
            </button>
            <button className="fa-btn" onClick={() => handleAiAction('expand')}>
              {aiLoading === 'expand' ? '...' : 'Expand'}
            </button>
            <button className="fa-btn" onClick={() => handleAiAction('tighten')}>
              {aiLoading === 'tighten' ? '...' : 'Tighten'}
            </button>
          </div>
        )}
      </div>

      {/* Loading indicator */}
      {aiLoading && (
        <div className="fa-narrative-ai-loading">
          <div className="fa-progress-spinner" style={{ width: 14, height: 14, borderWidth: 2, marginBottom: 0 }} />
          AI is {ACTION_PROGRESS[aiLoading] || 'working'}...
        </div>
      )}

      {error && <div className="fa-narrative-error">{error}</div>}

      {/* Bottom Toolbar */}
      <div className="fa-narrative-toolbar">
        <div className="fa-narrative-toolbar-left">
          {!content.trim() && (
            <button className="fa-btn fa-btn-sm fa-btn-primary" disabled={!!aiLoading || isLocked || !packageId} onClick={handleGenerateDraft}>
              {aiLoading === 'generate' ? 'Generating...' : 'Generate Draft'}
            </button>
          )}
          <button className="fa-btn fa-btn-sm fa-btn-action" disabled={!!aiLoading || isLocked} onClick={() => handleAiAction('polish')}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            AI Polish
          </button>
          <button className="fa-btn fa-btn-sm fa-btn-action" disabled={!!aiLoading || isLocked} onClick={() => handleAiAction('expand')}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
            Expand
            </button>
          <button className="fa-btn fa-btn-sm fa-btn-action" disabled={!!aiLoading || isLocked} onClick={() => handleAiAction('tighten')}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
            Tighten
          </button>
          <button className="fa-btn fa-btn-sm fa-btn-action" disabled={!!aiLoading || isLocked} onClick={() => handleAiAction('incorporate')}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
            AI Cleanup
          </button>
          <button className="fa-btn fa-btn-sm fa-btn-action" disabled={!!aiLoading || isLocked} onClick={() => handleAiAction('add_color')}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v18"/><path d="m5.64 7.64 12.72 8.72"/><path d="m5.64 16.36 12.72-8.72"/></svg>
            Add Color
          </button>
          <button className="fa-btn fa-btn-sm fa-btn-action" disabled={!!aiLoading || isLocked} onClick={loadVersions}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
            Revert
          </button>
        </div>
        <div className="fa-narrative-toolbar-right">
          <button className="fa-btn fa-btn-sm fa-btn-action" disabled={!!aiLoading || isLocked} onClick={() => handleAiAction('fact_check')}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            Fact-Check
          </button>

          {/* Version dropdown */}
          {showVersions && versions && (
            <div className="fa-version-dropdown">
              {versions.map((v) => (
                <div
                  key={v.id}
                  className="fa-version-item"
                  onClick={() => handleRevert(v.version)}
                >
                  <span>v{v.version} - {getField(v, 'changeType', 'change_type', 'edit')}</span>
                  <span className="fa-version-date">
                    {getField(v, 'createdAt', 'created_at') ? new Date(getField(v, 'createdAt', 'created_at')).toLocaleDateString() : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NarrativeCard;
